import { useCallback } from "react";
import { useUIContext } from "../UIContext";
import { useFilterContext } from "../FilterContext";
import { useDataContext } from "../DataContext";
import { useCatalogueUrlSync } from "./useCatalogueUrlSync";
import { buildFetchParams } from "../helpers/filterHelpers";

/**
 * Hook for managing UI state operations with URL synchronization
 * @returns {Object} Object containing UI state functions with URL sync
 */
export const useCatalogueUI = () => {
  const uiContext = useUIContext();
  const filterContext = useFilterContext();
  const dataContext = useDataContext();
  const { syncUrlState } = useCatalogueUrlSync();

  /**
   * Toggles between list and grid view and syncs URL
   */
  const toggleListView = useCallback(() => {
    const nextListView = !uiContext.uiState.listView;
    uiContext.toggleListView();
    const nextUi = { ...uiContext.uiState, listView: nextListView };
    syncUrlState(filterContext.filters, nextUi);
  }, [uiContext, filterContext.filters, syncUrlState]);

  /**
   * Sets the current page and re-applies filters with new page number
   * No API call needed - just re-filter existing data
   * @param {number} page - Page number to set
   */
  const setPage = useCallback(
    async (page) => {
      uiContext.setPage(page);
      const params = buildFetchParams(filterContext.filters, page);
      await dataContext.applyFiltersToData(params);
      const nextUi = { ...uiContext.uiState, page };
      syncUrlState(filterContext.filters, nextUi);
    },
    [uiContext, filterContext.filters, dataContext, syncUrlState],
  );

  return {
    toggleListView,
    toggleDetailsView: uiContext.toggleDetailsView,
    handleShowAdvancedFilter: uiContext.handleShowAdvancedFilter,
    setPage,
    getCoursesForCurrentPage: uiContext.getCoursesForCurrentPage,
  };
};
